import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, Building2, ShoppingBag, Users } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/ben2d0xw_Photoroom_20251219_184057.png";

// Contact Data
const CONTACTS = {
  mainLandline: '+97167796889',
  mainLandlineDisplay: '+971 6 779 6889',
  shopping: '+971551073662',
  shoppingDisplay: '+971 55 107 3662',
  management1: '+971502951774',
  management1Display: '+971 50 295 1774',
  management2: '+971502951773',
  management2Display: '+971 50 295 1773',
  email: 'info@tadaber-almostqbl.com',
};

const Footer = () => {
  const { t, isRTL, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.services'), path: '/services' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  const services = [
    t('services.nannies.title').split(' ')[0],
    t('services.housemaids.title'),
    t('services.security.title').split(' ')[0],
    t('services.cooks.title').split(' ')[0],
    t('services.drivers.title'),
    t('services.teachers.title').split(' ')[0],
    t('services.caregivers.title').split(' ')[0],
    t('services.farmers.title').split(' ')[0],
  ];

  return (
    <footer className="bg-card border-t border-border">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 ${isRTL ? 'text-right' : ''}`}>
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/">
              <img
                src={LOGO_URL}
                alt="TDABYR ALMSTQBL"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            {/* UAE Flag Accent */}
            <div className={`flex gap-1 ${isRTL ? 'justify-end' : ''}`}>
              <div className="w-8 h-1 bg-uae-red rounded-full" />
              <div className="w-8 h-1 bg-uae-green rounded-full" />
              <div className="w-8 h-1 bg-foreground rounded-full" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6 text-foreground">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-muted-foreground hover:text-uae-red transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6 text-foreground">{t('footer.ourServices')}</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-muted-foreground hover:text-uae-green transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6 text-foreground">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              {/* Main Office */}
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Building2 className="w-5 h-5 text-uae-green mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('footer.mainOffice')}</p>
                  <a
                    href={`tel:${CONTACTS.mainLandline}`}
                    className="text-foreground hover:text-uae-green transition-colors text-sm font-medium"
                  >
                    {CONTACTS.mainLandlineDisplay}
                  </a>
                </div>
              </li>
              
              {/* Shopping Dept */}
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ShoppingBag className="w-5 h-5 text-uae-red mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('footer.shoppingDept')}</p>
                  <a
                    href={`tel:${CONTACTS.shopping}`}
                    className="text-foreground hover:text-uae-red transition-colors text-sm font-medium"
                  >
                    {CONTACTS.shoppingDisplay}
                  </a>
                </div>
              </li>
              
              {/* Management */}
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Users className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('footer.management')}</p>
                  <a
                    href={`tel:${CONTACTS.management1}`}
                    className="text-foreground hover:text-accent transition-colors text-sm font-medium block"
                  >
                    {CONTACTS.management1Display}
                  </a>
                  <a
                    href={`tel:${CONTACTS.management2}`}
                    className="text-foreground hover:text-accent transition-colors text-sm font-medium block"
                  >
                    {CONTACTS.management2Display}
                  </a>
                </div>
              </li>
              
              {/* Email */}
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Mail className="w-5 h-5 text-uae-green mt-0.5 flex-shrink-0" />
                <a
                  href={`mailto:${CONTACTS.email}`}
                  className="text-muted-foreground hover:text-uae-green transition-colors text-sm break-all"
                >
                  {CONTACTS.email}
                </a>
              </li>
              
              {/* Working Hours */}
              <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-muted-foreground text-sm">{t('contact.workingHours')}</p>
                  <p className="text-uae-red text-sm">{t('contact.closedDay')}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border">
        <div className="container-custom py-6">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <p className="text-muted-foreground text-sm text-center md:text-left">
              © {currentYear} TDABYR ALMSTQBL FZE. {t('footer.allRights')}
            </p>
            <div className={`flex items-center gap-2 text-sm text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{t('footer.proudlyServing')}</span>
              <span className="font-semibold text-foreground">{t('footer.uae')}</span>
              {/* Mini UAE Flag */}
              <div className="flex gap-0.5 ml-1">
                <div className="w-4 h-3 bg-uae-red" />
                <div className="flex flex-col">
                  <div className="w-4 h-1 bg-uae-green" />
                  <div className="w-4 h-1 bg-foreground" />
                  <div className="w-4 h-1 bg-foreground" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
